import {
  Box,
  Breadcrumb,
  Button,
  Grid,
  GridColumn,
  Logo,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Margin, usePDF } from "react-to-pdf";

import { ErrorModal } from "@/components/ErrorModal/ErrorModal";
import ItemizedInvoiceItemsTable from "@/components/InvoicePay/ItemizedInvoiceItemsTable";
import NoteCard from "@/components/InvoicePay/NoteCard";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { useCreditDetail } from "@/hooks/useCreditDetail";
import { Credit, CreditType } from "@/types/Credit";
import {
  formatAddress,
  formatCreditStatus,
  formatCurrency,
  formatTimestampDate
} from "@/utils/InvoicePay";

const CreditDetailPage = () => {
  const { creditNo } = useParams<{ creditNo: string }>();
  const {
    fetchCredit,
    isLoading: isCreditLoading,
    error: creditDetailError
  } = useCreditDetail();

  const [credit, setCredit] = React.useState<Credit | null>(null);
  const [showErrorModal, setShowErrorModal] =
    React.useState(!!creditDetailError);

  const history = useHistory();
  const pages = [
    { pageId: "your-account", title: "Your Account" },
    {
      pageId: "your-account/invoices-and-payments",
      title: "Invoices and payments"
    },
    {
      pageId: `your-account/invoices-and-payments/credits/${creditNo}`,
      title: `Credit #${creditNo}`
    }
  ];

  React.useEffect(() => {
    const fetchCreditDetail = async () => {
      const creditDetail = await fetchCredit(creditNo);
      if (creditDetail) {
        setCredit(creditDetail);
      }
    };
    fetchCreditDetail();
  }, [creditNo, fetchCredit]);

  const handleClickPage = React.useCallback(
    (pageId: string) => {
      history.push(`/${pageId}`);
    },
    [history]
  );

  React.useEffect(() => {
    if (!isCreditLoading && creditDetailError) {
      setShowErrorModal(true);
    }
  }, [creditDetailError, isCreditLoading]);

  return (
    <ThemeProvider>
      <ErrorModal
        isVisible={showErrorModal}
        setVisible={setShowErrorModal}
        title={creditDetailError}
        onDismiss={() =>
          history.push("/your-account/invoices-and-payments?tab=credits")
        }
      />
      <Box
        sx={{
          backgroundColor: "surface-neutral-secondary",
          pt: "spacing-9",
          pb: "spacing-12"
        }}
      >
        <Grid>
          <GridColumn sx={{ pb: "spacing-9" }}>
            <Breadcrumb pages={pages} onClickPage={handleClickPage} />
          </GridColumn>
        </Grid>
        {isCreditLoading ? (
          <LoadingSpinner size="xl" />
        ) : !credit ? (
          <Grid>
            <GridColumn>
              <Text sx={{ pt: "spacing-6" }}>Credit not found.</Text>
            </GridColumn>
          </Grid>
        ) : (
          <CreditDetailContent credit={credit} />
        )}
      </Box>
    </ThemeProvider>
  );
};

const CreditDetailContent: React.FC<{ credit: Credit }> = ({ credit }) => {
  const { toPDF, targetRef } = usePDF({
    filename: `Moderna_Credit_${credit?.creditNo}.pdf`,
    page: {
      margin: Margin.SMALL,
      format: "A4"
    }
  });

  if (!credit) return null;

  return (
    <Grid>
      <GridColumn>{formatCreditStatus(credit.status)}</GridColumn>
      <GridColumn>
        <CreditHeader
          creditNo={credit.creditNo}
          creditType={credit.type}
          onClickDownload={toPDF}
        />
      </GridColumn>
      <GridColumn>
        <Box
          ref={targetRef}
          sx={{
            backgroundColor: "white",
            padding: "spacing-8",
            display: "flex",
            flexDirection: "column",
            borderRadius: "radius-2"
          }}
        >
          <Logo logo="moderna-logo" sx={{ height: 16, width: 48 }} />
          <Grid>
            <GridColumn>
              <Text weight="bold">
                P.O. number:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {credit.poNumber || "--"}
                </Text>
              </Text>
              <Text weight="bold">
                Date issued:
                <Text weight="regular" sx={{ pl: "spacing-2" }}>
                  {formatTimestampDate(credit.creditDate)}
                </Text>
              </Text>
            </GridColumn>
          </Grid>

          {/* Shipping and billing addresses */}
          <Grid sx={{ py: "spacing-9" }}>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Ship to</Text>
              <Text>{formatAddress(credit.shipTo)}</Text>
              <Text weight="bold">
                Account number: <Text weight="regular">{credit.shipTo.id}</Text>
              </Text>
            </GridColumn>
            <GridColumn size={2} fillGapEnd={true}>
              <Text weight="bold">Billed to</Text>
              <Text>{formatAddress(credit.billTo)}</Text>
              <Text weight="bold">
                Account number: <Text weight="regular">{credit.billTo.id}</Text>
              </Text>
            </GridColumn>
          </Grid>

          {credit.orderLineItems.length > 0 ? (
            <ItemizedInvoiceItemsTable items={credit.orderLineItems} />
          ) : credit.notes.length > 0 ? (
            <NoteCard notes={credit.notes} />
          ) : (
            <Text>
              The order associated with this credit was placed outside of
              Moderna Direct, so detailed information is not available.
            </Text>
          )}

          {/* Credit invoice total */}
          <Grid sx={{ pt: "spacing-9" }}>
            <GridColumn
              size={3}
              push={7}
              fillGapEnd={true}
              sx={{ alignItems: "end", color: "color-current-brand-800" }}
            >
              <Text weight="medium" size="font-size-7">
                Credit invoice total
              </Text>
            </GridColumn>
            <GridColumn
              size={2}
              sx={{ alignItems: "end", color: "color-current-brand-800" }}
            >
              <Text weight="medium" size="font-size-7">
                {formatCurrency(credit.total)}
              </Text>
            </GridColumn>
          </Grid>
        </Box>
      </GridColumn>
    </Grid>
  );
};

const CreditHeader: React.FC<{
  creditNo: string;
  creditType: CreditType;
  onClickDownload: () => void;
}> = ({ creditNo, creditType, onClickDownload }) => {
  const headerLabel =
    creditType === CreditType.PartialCredit ? "Remaining credit" : "Credit";
  return (
    <Grid sx={{ pt: "spacing-2", pb: "spacing-6" }}>
      <GridColumn size={6}>
        <Box sx={{ textAlign: "start" }}>
          <Text variant="h4">
            {headerLabel} #{creditNo}
          </Text>
        </Box>
      </GridColumn>
      <GridColumn size={3} push={3}>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outline"
            icon="download-simple"
            iconPosition="end"
            onClick={onClickDownload}
          >
            Download
          </Button>
        </Box>
      </GridColumn>
    </Grid>
  );
};

export default CreditDetailPage;
